import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/state/authStore';
import { useGameStore } from '@/state/gameStore';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Phase 16.3.2 — Schützt die `/play`-Route.
 *
 * Logik:
 *   - Kein Token       → Redirect auf /play/login
 *   - Token vorhanden  → verify-Call gegen /api/auth/verify
 *     - 200 OK         → Children rendern
 *     - 401            → Token löschen, Redirect auf /play/login
 *     - Network-Fehler → Toast „Offline-Modus", Children mit lokalem State rendern
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const verifySession = useAuthStore((s) => s.verifySession);
  const pushToast = useGameStore((s) => s.pushToast);

  const [verified, setVerified] = useState<'pending' | 'ok' | 'unauth' | 'offline'>(
    'pending',
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthenticated) {
        if (!cancelled) setVerified('unauth');
        return;
      }
      try {
        const ok = await verifySession();
        if (cancelled) return;
        if (ok) {
          setVerified('ok');
        } else if (useAuthStore.getState().isAuthenticated) {
          // verify hat false ohne Logout zurückgeliefert → Network-Fehler
          setVerified('offline');
          pushToast({
            kind: 'warning',
            text: 'Verbindungsfehler — Offline-Modus, lokaler Spielstand wird verwendet.',
          });
        } else {
          setVerified('unauth');
        }
      } catch {
        if (!cancelled) setVerified('offline');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (verified === 'pending') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-primary text-fg-muted">
        <p className="font-display text-sm">Prüfe Session …</p>
      </main>
    );
  }
  if (verified === 'unauth') {
    return <Navigate to="/play/login" replace />;
  }
  // ok oder offline → rendert Game (offline mit Banner-Toast bereits gepusht)
  return <>{children}</>;
}
